import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { EmailChannelPolicyApiService } from '../services/email-channel-policy-api.service';
import { EmailChannelPolicyDrawerService } from '../services/email-channel-policy-drawer.service';
import {
  EmailChannelPolicy,
  EmailPolicyChannelType,
} from '../models/email-channel-policy.model';
import { BusinessApiService } from '../../businesses/services/business-api.service';

@Component({
  selector: 'app-email-channel-policy-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
    BadgeComponent,
  ],
  templateUrl: './email-channel-policy-list.component.html',
})
export class EmailChannelPolicyListComponent implements OnInit, OnDestroy {
  private all: EmailChannelPolicy[] = [];
  items: EmailChannelPolicy[] = [];
  loading = false;
  error = '';

  filtroChannel: '' | EmailPolicyChannelType = '';
  filtroEnabled: '' | 'true' | 'false' = '';
  filtroScope: '' | 'global' | 'business' = '';

  private readonly destroy$ = new Subject<void>();

  /** id de empresa → nombre comercial, para no pintar "#12" en la tabla ni en el diálogo. */
  private readonly businessNames = new Map<number, string>();

  private readonly channelLabels: Record<EmailPolicyChannelType, string> = {
    SYSTEM_API: 'Sistema (API)',
    USER_GMAIL: 'Gmail',
    USER_MICROSOFT: 'Outlook / Microsoft',
    USER_SMTP: 'SMTP',
    USER_API: 'API de usuario',
  };

  readonly channelOptions: { value: EmailPolicyChannelType; label: string }[] = [
    { value: 'SYSTEM_API', label: 'Sistema (API)' },
    { value: 'USER_GMAIL', label: 'Gmail' },
    { value: 'USER_MICROSOFT', label: 'Outlook / Microsoft' },
    { value: 'USER_SMTP', label: 'SMTP' },
    { value: 'USER_API', label: 'API de usuario' },
  ];

  constructor(
    private api: EmailChannelPolicyApiService,
    private businessApi: BusinessApiService,
    private drawerService: EmailChannelPolicyDrawerService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.cargarNombresDeEmpresa();
    this.cargar();
    this.drawerService.saved$.pipe(takeUntil(this.destroy$)).subscribe(() => this.cargar());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  cargar(): void {
    this.loading = true;
    this.error = '';
    this.api.list().subscribe({
      next: (data) => {
        this.all = data ?? [];
        this.applyFilters();
        this.loading = false;
      },
      error: (err) => {
        this.error = err?.error?.message || 'Error cargando las políticas de canal';
        this.loading = false;
      },
    });
  }

  applyFilters(): void {
    let data = [...this.all];
    if (this.filtroChannel) {
      data = data.filter((p) => p.channelType === this.filtroChannel);
    }
    if (this.filtroEnabled !== '') {
      const want = this.filtroEnabled === 'true';
      data = data.filter((p) => !!p.enabled === want);
    }
    if (this.filtroScope === 'global') {
      data = data.filter((p) => p.intIdBussines === null);
    } else if (this.filtroScope === 'business') {
      data = data.filter((p) => p.intIdBussines !== null);
    }
    this.items = data.sort((a, b) => a.channelType.localeCompare(b.channelType));
  }

  channelLabel(type: EmailPolicyChannelType): string {
    return this.channelLabels[type] ?? type;
  }

  /**
   * Nombre de la empresa dueña de la política. La pantalla es de Super Admin y el
   * backend solo devuelve el id, así que se resuelve contra el catálogo de empresas.
   * Si la lista no se pudo leer, se dice "Empresa específica" — nunca el id crudo.
   */
  businessLabel(id: number | null | undefined): string {
    if (id === null || id === undefined) return 'Global (todas las empresas)';
    return this.businessNames.get(id) ?? 'Empresa específica';
  }

  /** Catálogo de empresas para traducir `intIdBussines` a un nombre. Falla en silencio. */
  private cargarNombresDeEmpresa(): void {
    this.businessApi
      .getAll(0, 200)
      .pipe(catchError(() => of(null)), takeUntil(this.destroy$))
      .subscribe((res) => {
        (res?.content ?? []).forEach((b) => this.businessNames.set(b.id, b.name));
      });
  }

  nuevo(): void {
    this.drawerService.open();
  }

  editar(policy: EmailChannelPolicy): void {
    this.drawerService.open({ policyId: policy.id, policy });
  }

  async eliminar(policy: EmailChannelPolicy): Promise<void> {
    const scope = policy.intIdBussines === null ? 'Global' : this.businessLabel(policy.intIdBussines);
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar política de canal',
      message: `¿Eliminar la política de "${this.channelLabel(policy.channelType)}" (${scope})?`,
    });
    if (!ok) return;
    this.api.delete(policy.id).subscribe({
      next: () => {
        this.cargar();
        this.toast.deleted('Política de canal');
      },
      error: (err) => {
        this.toast.error({
          title: 'Error',
          message: err?.error?.message || 'Error al eliminar la política de canal',
        });
      },
    });
  }

  trackById = (_: number, item: EmailChannelPolicy) => item.id;
}
